import { useEffect, useMemo, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Switch,
  TableFooter,
  FormControlLabel,
  TablePagination,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LinkIcon from '@mui/icons-material/Link';
import GroupIcon from '@mui/icons-material/Group';

import { useCatch, useEffectAsync } from '../reactHelper';
import { formatBoolean, formatTime } from '../common/util/formatter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionFab from './components/CollectionFab';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';
import { useManager } from '../common/util/permissions';
import SearchHeader from './components/SearchHeader';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_ROWS_PER_PAGE = 50;
const SEARCH_DEBOUNCE = 300;

const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

const buildSearchIndex = (user) => [
  user.name,
  user.email,
  user.phone,
  user.deviceLimit,
  user.userLimit,
  user.expirationTime,
  user.administrator ? 'true yes sim admin administrator' : 'false no não user usuário',
  user.disabled ? 'true yes sim disabled desabilitado' : 'false no não enabled habilitado',
  user.temporary ? 'true yes sim temporary temporário' : 'false no não permanent permanente',
].filter(Boolean).join(' ').toLowerCase();

const matchesKeyword = (indexedText, keyword) => {
  if (!keyword) return true;

  const tokens = keyword
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (!tokens.length) return true;

  return tokens.every((token) => indexedText.includes(token));
};

const UserRow = memo(({
  item,
  classes,
  t,
  manager,
  setTimestamp,
  actionLogin,
  actionConnections,
}) => (
  <TableRow hover>
    <TableCell>{item.name}</TableCell>
    <TableCell>{item.email}</TableCell>
    <TableCell>{formatBoolean(item.administrator, t)}</TableCell>
    <TableCell>{formatBoolean(item.disabled, t)}</TableCell>
    <TableCell>{formatTime(item.expirationTime, 'date')}</TableCell>
    <TableCell className={classes.columnAction} padding="none">
      <CollectionActions
        itemId={item.id}
        editPath="/settings/user"
        endpoint="users"
        setTimestamp={setTimestamp}
        customActions={manager ? [actionLogin, actionConnections] : [actionConnections]}
      />
    </TableCell>
  </TableRow>
));

UserRow.displayName = 'UserRow';

const UsersKpi = memo(({ totalUsers, filteredUsers, hasActiveFilter, t }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'action.hover',
        }}
      >
        <GroupIcon fontSize="medium" />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('settingsUsers') || 'Usuários'}
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          {totalUsers.toLocaleString('pt-BR')}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {hasActiveFilter
            ? `${filteredUsers.toLocaleString('pt-BR')} exibido(s) no filtro`
            : 'Total de usuários cadastrados'}
        </Typography>
      </Box>
    </Box>
  </Paper>
));

UsersKpi.displayName = 'UsersKpi';

const UsersPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const t = useTranslation();
  const manager = useManager();

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [temporary, setTemporary] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedKeyword(normalizeText(searchKeyword));
      setPage(0);
    }, SEARCH_DEBOUNCE);

    return () => window.clearTimeout(timeout);
  }, [searchKeyword]);

  const handleLogin = useCatch(async (userId) => {
    await fetchOrThrow(`/api/session/${userId}`);
    window.location.replace('/');
  });

  const actionLogin = useMemo(() => ({
    key: 'login',
    title: t('loginLogin'),
    icon: <LoginIcon fontSize="small" />,
    handler: handleLogin,
  }), [t, handleLogin]);

  const actionConnections = useMemo(() => ({
    key: 'connections',
    title: t('sharedConnections'),
    icon: <LinkIcon fontSize="small" />,
    handler: (userId) => navigate(`/settings/user/${userId}/connections`),
  }), [t, navigate]);

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/users?excludeAttributes=true');
      const data = await response.json();

      const indexedUsers = data.map((user) => ({
        ...user,
        _searchIndex: buildSearchIndex(user),
      }));

      setItems(indexedUsers);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  const filteredItems = useMemo(() => (
    items.filter((user) => {
      if (!temporary && user.temporary) {
        return false;
      }
      return matchesKeyword(user._searchIndex, debouncedKeyword);
    })
  ), [items, temporary, debouncedKeyword]);

  const totalCount = filteredItems.length;
  const totalUsers = items.length;
  const hasActiveFilter = Boolean(debouncedKeyword) || !temporary;

  const safePage = useMemo(() => {
    if (totalCount === 0) {
      return 0;
    }

    const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
    return Math.min(page, maxPage);
  }, [page, rowsPerPage, totalCount]);

  const paginatedItems = useMemo(() => {
    const startIndex = safePage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, safePage, rowsPerPage]);

  const handleTemporaryChange = (event) => {
    setTemporary(event.target.checked);
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReload = () => {
    setTimestamp(Date.now());
  };

  return (
    <PageLayout
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'settingsUsers']}
    >
      {!loading && (
        <UsersKpi
          totalUsers={totalUsers}
          filteredUsers={totalCount}
          hasActiveFilter={hasActiveFilter}
          t={t}
        />
      )}

      <SearchHeader
        keyword={searchKeyword}
        setKeyword={setSearchKeyword}
      />

      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedName')}</TableCell>
            <TableCell>{t('userEmail')}</TableCell>
            <TableCell>{t('userAdmin')}</TableCell>
            <TableCell>{t('sharedDisabled')}</TableCell>
            <TableCell>{t('userExpirationTime')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableShimmer columns={6} endAction />
          ) : (
            paginatedItems.map((item) => (
              <UserRow
                key={item.id}
                item={item}
                classes={classes}
                t={t}
                manager={manager}
                setTimestamp={handleReload}
                actionLogin={actionLogin}
                actionConnections={actionConnections}
              />
            ))
          )}

          {!loading && paginatedItems.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} align="center">
                {t('sharedNoData')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TableRow>
            <TableCell colSpan={4}>
              {!loading && (
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={safePage}
                  onPageChange={handlePageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  labelRowsPerPage="Itens por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
              )}
            </TableCell>

            <TableCell colSpan={2} align="right">
              <FormControlLabel
                control={(
                  <Switch
                    checked={temporary}
                    onChange={handleTemporaryChange}
                    size="small"
                  />
                )}
                label={t('userTemporary')}
                labelPlacement="start"
              />
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <CollectionFab editPath="/settings/user" />
    </PageLayout>
  );
};

export default UsersPage;